import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import { statusOptionsFor } from "modules/appointments/helper/appointmentFilters";
import UserService from "modules/user/service/UserService";

/** Figma 5868:3607 — the sliders glyph on the filter button. */
const FilterGlyph = ({ active }) => (
  <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path
      d="M2 5h8M14 5h2M2 13h2M8 13h8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="5" r="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="6" cy="13" r="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    {active && <circle cx="15.5" cy="2.5" r="2.5" fill="currentColor" stroke="none" />}
  </svg>
);
FilterGlyph.propTypes = { active: PropTypes.bool };

/**
 * The quick status filter — a scrolling strip of pills, then the button that
 * opens the full sheet.
 *
 * It is a SHORTCUT, not a second filter. Every pill calls the same
 * `setColumnFilter("status", …)` the desktop column funnel calls, with the same
 * option ids, so the two cannot drift and neither knows about the other.
 *
 * Which pill lights up is read back from the applied value rather than kept in
 * its own state, and that is what keeps it honest about what the sheet can do:
 *
 *   nothing selected  -> "All"
 *   exactly one       -> that status
 *   two or more       -> NONE, because no single pill describes the filter
 *
 * The third case is reachable from the sheet's multi-select. Lighting up the
 * first of several would claim a filter that is not the one in force.
 */
const MobileStatusRow = ({ value, onPick, onOpenFilters, filtersActive }) => {
  /* The same per-role list the desktop funnel offers, read the same way it
     reads it (AppointmentsDashboard) — the pills are a shortcut to that
     filter, so the two must not drift. Read once: the signed-in user cannot
     change without a full reload. */
  const statusOptions = useMemo(
    () => statusOptionsFor(new UserService().doctorCategory()),
    []
  );

  const railRef = useRef(null);
  const activeRef = useRef(null);
  const picked = value || [];
  const activeId = picked.length === 1 ? picked[0] : picked.length === 0 ? "__all" : null;

  /* Keep the lit pill in view when the filter was set from the sheet rather
     than from this strip. Adjusts the rail's own scrollLeft — scrollIntoView
     would drag the page vertically too. */
  useEffect(() => {
    const rail = railRef.current;
    const pill = activeRef.current;
    if (!rail || !pill) return;
    const railBox = rail.getBoundingClientRect();
    const pillBox = pill.getBoundingClientRect();
    const pad = 16;
    if (pillBox.left < railBox.left + pad) {
      rail.scrollLeft -= railBox.left + pad - pillBox.left;
    } else if (pillBox.right > railBox.right - pad) {
      rail.scrollLeft += pillBox.right - (railBox.right - pad);
    }
  }, [activeId]);

  const pill = (id, label) => {
    const on = activeId === id;
    return (
      <button
        key={id}
        ref={on ? activeRef : null}
        type="button"
        className={`zym-spill${on ? " is-active" : ""}`}
        aria-pressed={on}
        onClick={() => onPick(id === "__all" ? null : [id])}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="zym-statusbar">
      <div className="zym-spills" ref={railRef}>
        {pill("__all", "All")}
        {statusOptions.map((o) => pill(o.id, o.label))}
      </div>

      <button
        type="button"
        className={`zym-filter-btn${filtersActive ? " is-active" : ""}`}
        onClick={onOpenFilters}
        aria-label="Filters"
      >
        <FilterGlyph active={filtersActive} />
      </button>
    </div>
  );
};

MobileStatusRow.propTypes = {
  /** The APPLIED status filter — an array of status option ids, or undefined. */
  value: PropTypes.array,
  /** (ids|null) => void — straight through to setColumnFilter("status", …). */
  onPick: PropTypes.func.isRequired,
  onOpenFilters: PropTypes.func.isRequired,
  /** Any filter at all in force, so the button can show a dot. */
  filtersActive: PropTypes.bool,
};

export default MobileStatusRow;
